import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { Users, Plus, Check, X, Edit2, Trash2, ExternalLink, AlertCircle, Clock, Lock, RotateCcw, Eye, Search, Upload, Paperclip, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const INDUSTRY_OPTIONS = [
  'Healthcare',
  'FinTech',
  'Real Estate/PropTech',
  'EdTech',
  'Consulting/Enterprise',
  'SaaS/General',
  'B2C'
];

const CLASSIFICATION_LABELS = { hot: 'Hot', warm: 'Warm', cold: 'Cold' };
const CLASSIFICATION_BADGE = { hot: 'badge-red', warm: 'badge-amber', cold: 'badge-blue' };

// Source badge for the leads table. Both Upwork variants collapse to one
// "Upwork" pill (the LinkedIn/Upwork split is the level users care about).
const SOURCE_BADGE = {
  linkedin: { label: 'LinkedIn', cls: 'badge-gray' },
  upwork_inbound: { label: 'Upwork', cls: 'badge-blue' },
  upwork_research: { label: 'Upwork', cls: 'badge-blue' },
};
// Source filter tabs. key is the API `source` bucket ('' = no filter).
const SOURCE_TABS = [
  { key: '', label: 'All', countKey: 'all' },
  { key: 'linkedin', label: 'LinkedIn', countKey: 'linkedin' },
  { key: 'upwork', label: 'Upwork', countKey: 'upwork' },
];

// Phase 3: USD formatter for the Deal Value column.
// Returns '—' for null/undefined/empty to match the rest of the table's
// missing-data convention.
const formatUSD = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const LEAD_STATUS_LABELS = {
  active: 'Active',
  discovery_scheduled: 'Discovery Scheduled',
  discovery_done: 'Discovery Done',
  proposal_sent: 'Proposal Sent',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  not_interested: 'Not Interested',
  dead_cold: 'Dead Cold',
  parked: 'Parked'
};
const LEAD_STATUS_BADGE = {
  active: 'badge-gray',
  discovery_scheduled: 'badge-blue',
  discovery_done: 'badge-blue',
  proposal_sent: 'badge-blue',
  negotiation: 'badge-amber',
  closed_won: 'badge-green',
  closed_lost: 'badge-red',
  not_interested: 'badge-amber',
  dead_cold: 'badge-gray',
  parked: 'badge-amber'
};

// Server-side pagination page size. Must stay <= the backend's hard cap (200).
const PAGE_SIZE = 10;

const todayISO = () => new Date().toISOString().split('T')[0];

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRelative = (ts) => {
  if (!ts) return '';
  const then = new Date(ts);
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return `on ${then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

// ============================================================
// PHASE 4b — document staging during lead creation
// Constants mirror DocumentsCard (and the backend allowlist).
// Kept in sync manually; backend is authoritative.
// ============================================================
const MAX_FILE_SIZE = 25 * 1024 * 1024;  // 25 MB
const MAX_FILES_PER_UPLOAD = 5;

const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.txt',
  '.xls', '.xlsx', '.csv',
  '.ppt', '.pptx',
  '.png', '.jpg', '.jpeg',
  '.zip',
];

const ACCEPT_ATTR = ALLOWED_EXTENSIONS.join(',');

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const getExtension = (filename) => {
  const idx = (filename || '').lastIndexOf('.');
  return idx >= 0 ? filename.slice(idx).toLowerCase() : '';
};

const Leads = () => {
  const navigate = useNavigate();
  const { user, isReadOnly, canAccessUpwork } = useAuth();
  const isReviewer = ['admin', 'team_lead'].includes(user?.role);
  // Viewer (co-founder) sees the reviewer layout (tabs, all-leads view,
  // BDE column) but cannot mutate. canSeeAllLeads gates structure;
  // isReadOnly gates buttons that mutate.
  const canSeeAllLeads = isReviewer || isReadOnly;

  // Persist filter/scroll state across navigation. URL params take
  // precedence — used by the Lead Performance page's stalled drill-down
  // which navigates to /leads?lead_status=<stage>&stalled=1.
  const STATE_KEY = 'leadsPageState';
  const init = (() => {
    let stored = {};
    try { stored = JSON.parse(sessionStorage.getItem(STATE_KEY) || '{}'); }
    catch { stored = {}; }
    const url = new URLSearchParams(window.location.search);
    const fromUrl = {};
    if (url.has('lead_status'))   fromUrl.filterLeadStatus = url.get('lead_status');
    if (url.has('classification')) fromUrl.filterClassification = url.get('classification');
    if (url.has('bde_id'))        fromUrl.filterBde = url.get('bde_id');
    if (url.has('stalled'))       fromUrl.filterStalled = url.get('stalled') === '1' || url.get('stalled') === 'true';
    if (url.has('source'))        fromUrl.filterSource = url.get('source');
    return { ...stored, ...fromUrl };
  })();

  const [activeTab, setActiveTab] = useState(init.activeTab || (canSeeAllLeads ? 'all' : 'mine'));
  const [myLeads, setMyLeads] = useState([]);
  const [pendingLeads, setPendingLeads] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    prospect_name: '',
    linkedin_url: '',
    company: '',
    industry: '',
    classification: '',
    notes: '',
    lead_date: todayISO(),
    source: 'linkedin',
    // Required only when source is an Upwork variant (enforced in validateForm
    // + backend). Stays '' for LinkedIn leads.
    upwork_job_url: '',
    // Connection-note tracking. '' = not yet picked (create requires a choice);
    // true = Yes, false = No, null = "Not set" (only meaningful when editing an
    // un-backfilled existing lead).
    is_note_sent: '',
    connection_note: '',
    // AI Rescue flag + conditional discovery source. When the toggle is off,
    // both source fields stay empty (backend forces them NULL on save).
    // ai_rescue_source: '' until a choice is made; ai_rescue_source_other is
    // only used when the source is 'Other'.
    is_ai_rescue: false,
    ai_rescue_source: '',
    ai_rescue_source_other: '',
  });
  const [industryIsOther, setIndustryIsOther] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Phase 4b — file staging during lead creation
  const [stagedFiles, setStagedFiles] = useState([]);
  const [stagedNote, setStagedNote] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const stagedFileInputRef = useRef(null);

  const [validatingLead, setValidatingLead] = useState(null);
  const [validationClassification, setValidationClassification] = useState('');
  const [reclassificationNote, setReclassificationNote] = useState('');

  const [filterClassification, setFilterClassification] = useState(init.filterClassification || '');
  const [filterLeadStatus, setFilterLeadStatus] = useState(init.filterLeadStatus || 'active,discovery_scheduled,discovery_done,proposal_sent,negotiation');
  const [filterBde, setFilterBde] = useState(init.filterBde || '');
  const [filterStalled, setFilterStalled] = useState(init.filterStalled === true);
  // Source tab: '' = All, 'linkedin', or 'upwork' (spans both Upwork variants).
  const [filterSource, setFilterSource] = useState(init.filterSource || '');
  // AI Rescue filters: '' = all, '1' = rescue on, '0' = rescue off. Source: ''
  // = any, or one of LinkedIn/Upwork/Other.
  const [filterAiRescue, setFilterAiRescue] = useState(init.filterAiRescue || '');
  const [filterAiRescueSource, setFilterAiRescueSource] = useState(init.filterAiRescueSource || '');
  // Per-source totals for the tab labels, returned by GET /leads.
  const [sourceCounts, setSourceCounts] = useState({ all: 0, linkedin: 0, upwork: 0 });
  const [bdeList, setBdeList] = useState([]);
  const [searchQuery, setSearchQuery] = useState(init.searchQuery || '');

  // Server-side pagination. Each tab tracks its own page; meta (total +
  // total_pages) comes back from GET /leads. Page resets to 1 whenever a
  // filter or the search term changes (see the reset effect below).
  const [allLeadsPage, setAllLeadsPage] = useState(1);
  const [allLeadsMeta, setAllLeadsMeta] = useState({ total: 0, total_pages: 1 });
  const [myLeadsPage, setMyLeadsPage] = useState(1);
  const [myLeadsMeta, setMyLeadsMeta] = useState({ total: 0, total_pages: 1 });

  // Persist filter state on every change (preserves scrollY from last save)
  useEffect(() => {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({
        activeTab, filterClassification, filterLeadStatus, filterBde, filterStalled, filterSource,
        filterAiRescue, filterAiRescueSource, searchQuery,
        scrollY: init.scrollY,
      }));
    } catch {}
  }, [activeTab, filterClassification, filterLeadStatus, filterBde, filterStalled, filterSource, filterAiRescue, filterAiRescueSource, searchQuery]);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && init.scrollY) {
      window.scrollTo(0, init.scrollY);
    }
  }, [loading]);

  const fetchMyLeads = async () => {
    try {
      const params = { page: myLeadsPage, page_size: PAGE_SIZE };
      if (filterLeadStatus) params.lead_status = filterLeadStatus;
      if (searchQuery) params.q = searchQuery;
      if (filterStalled) params.stalled = 1;
      if (filterSource && canAccessUpwork) params.source = filterSource;
      if (filterAiRescue) params.is_ai_rescue = filterAiRescue;
      if (filterAiRescueSource) params.ai_rescue_source = filterAiRescueSource;
      const res = await api.get('/leads', { params });
      setMyLeads(res.data?.leads || []);
      if (res.data?.counts) setSourceCounts(res.data.counts);
      const total = res.data?.total ?? 0;
      const total_pages = res.data?.total_pages ?? 1;
      setMyLeadsMeta({ total, total_pages });
      // If the current page fell off the end (e.g. the last row on it was
      // deleted), snap back to the last valid page — the effect refetches.
      if (myLeadsPage > total_pages) setMyLeadsPage(total_pages);
    } catch (err) {
      console.error('Fetch my leads error:', err);
      setError('Could not load your leads.');
    }
  };

  const fetchPendingLeads = async () => {
    try {
      const res = await api.get('/leads/pending-review');
      setPendingLeads(res.data?.leads || []);
    } catch (err) {
      console.error('Fetch pending leads error:', err);
    }
  };

  const fetchAllLeads = async () => {
    try {
      const params = { page: allLeadsPage, page_size: PAGE_SIZE };
      if (filterClassification) params.classification = filterClassification;
      if (filterLeadStatus) params.lead_status = filterLeadStatus;
      if (filterBde) params.bde_id = filterBde;
      if (searchQuery) params.q = searchQuery;
      if (filterStalled) params.stalled = 1;
      if (filterSource && canAccessUpwork) params.source = filterSource;
      if (filterAiRescue) params.is_ai_rescue = filterAiRescue;
      if (filterAiRescueSource) params.ai_rescue_source = filterAiRescueSource;
      const res = await api.get('/leads', { params });
      setAllLeads(res.data?.leads || []);
      if (res.data?.counts) setSourceCounts(res.data.counts);
      // BDE dropdown list now comes from the server (stable across pages)
      // rather than being derived from the current page of rows.
      setBdeList(res.data?.bdes || []);
      const total = res.data?.total ?? 0;
      const total_pages = res.data?.total_pages ?? 1;
      setAllLeadsMeta({ total, total_pages });
      if (allLeadsPage > total_pages) setAllLeadsPage(total_pages);
    } catch (err) {
      console.error('Fetch all leads error:', err);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    if (canSeeAllLeads) {
      await Promise.all([fetchMyLeads(), fetchPendingLeads(), fetchAllLeads()]);
    } else {
      await fetchMyLeads();
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  // Any filter/search change resets both tabs back to page 1. Kept separate
  // from the fetch effects below so a page-button click (which changes only
  // the page) doesn't get reset. Rapid changes collapse into one fetch via the
  // 300ms debounce + cleanup on the fetch effects.
  useEffect(() => {
    setAllLeadsPage(1);
    setMyLeadsPage(1);
  }, [filterClassification, filterLeadStatus, filterBde, searchQuery, filterStalled, filterSource, filterAiRescue, filterAiRescueSource]);

  // Debounced refetch when filters, search, or page change (All Leads tab)
  useEffect(() => {
    const t = setTimeout(() => {
      if (canSeeAllLeads && activeTab === 'all') fetchAllLeads();
    }, 300);
    return () => clearTimeout(t);
  }, [filterClassification, filterLeadStatus, filterBde, searchQuery, filterStalled, filterSource, filterAiRescue, filterAiRescueSource, allLeadsPage]);

  // Debounced refetch when search, status filter, or page change (My Leads tab)
  useEffect(() => {
    const t = setTimeout(() => {
      if (!canSeeAllLeads || activeTab === 'mine') fetchMyLeads();
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, filterLeadStatus, filterStalled, filterSource, filterAiRescue, filterAiRescueSource, myLeadsPage]);

  const resetForm = () => {
    setFormData({
      prospect_name: '', linkedin_url: '', company: '', industry: '',
      classification: '', notes: '', lead_date: todayISO(), source: 'linkedin',
      upwork_job_url: '', is_note_sent: '', connection_note: '',
      is_ai_rescue: false, ai_rescue_source: '', ai_rescue_source_other: '',
    });
    setFormError('');
    setEditingLead(null);
    setIndustryIsOther(false);
    // Phase 4b — clear staged files
    setStagedFiles([]);
    setStagedNote('');
    setUploadProgress('');
    if (stagedFileInputRef.current) stagedFileInputRef.current.value = '';
  };

  const openAddForm = () => { resetForm(); setShowAddForm(true); };

  const openEditForm = (lead) => {
    setEditingLead(lead);
    setFormData({
      prospect_name: lead.prospect_name,
      linkedin_url: lead.linkedin_url,
      company: lead.company,
      industry: lead.industry || '',
      classification: lead.classification,
      notes: lead.notes || '',
      lead_date: lead.lead_date?.split('T')[0] || todayISO(),
      source: lead.source || 'linkedin',
      upwork_job_url: lead.upwork_job_url || '',
      // MySQL returns tinyint 1/0/null — normalize to true/false/null.
      is_note_sent: lead.is_note_sent === null || lead.is_note_sent === undefined
        ? null
        : !!lead.is_note_sent,
      connection_note: lead.connection_note || '',
      // MySQL returns tinyint 1/0 — normalize to boolean. Source columns are
      // NULL when the flag is off; coerce to '' for the controlled dropdown.
      is_ai_rescue: !!lead.is_ai_rescue,
      ai_rescue_source: lead.ai_rescue_source || '',
      ai_rescue_source_other: lead.ai_rescue_source_other || '',
    });
    setFormError('');
    setIndustryIsOther(lead.industry && !INDUSTRY_OPTIONS.includes(lead.industry));
    setShowAddForm(true);
  };

  const closeAddForm = () => { setShowAddForm(false); resetForm(); };

  const validateForm = () => {
    if (!formData.prospect_name.trim()) return 'Prospect name is required.';
    if (!formData.linkedin_url.trim()) return 'LinkedIn URL is required.';
    if (!formData.linkedin_url.toLowerCase().includes('linkedin.com')) {
      return 'LinkedIn URL must be a valid linkedin.com link.';
    }
    if (!formData.company.trim()) return 'Company is required.';
    if (!formData.classification) return 'Classification is required (Hot / Warm / Cold).';
    // Upwork-sourced leads must carry the originating Upwork job URL.
    if ((formData.source === 'upwork_inbound' || formData.source === 'upwork_research')
        && !formData.upwork_job_url.trim()) {
      return 'Upwork Job URL is required for Upwork leads.';
    }
    // Connection-note tracking. On CREATE a Yes/No choice is mandatory; on EDIT
    // "Not set" (null) is allowed for un-backfilled leads. When Yes, the note text
    // is required in both modes.
    if (!editingLead && formData.is_note_sent !== true && formData.is_note_sent !== false) {
      return 'Please select whether a connection note was sent.';
    }
    if (formData.is_note_sent === true && !formData.connection_note.trim()) {
      return 'Please paste the note message.';
    }
    // AI Rescue: when on, a discovery source is required; 'Other' additionally
    // requires the free-text detail. (Off clears both — nothing to validate.)
    if (formData.is_ai_rescue) {
      if (!formData.ai_rescue_source) {
        return 'Please select where you found this AI Rescue lead.';
      }
      if (formData.ai_rescue_source === 'Other' && !formData.ai_rescue_source_other.trim()) {
        return 'Please specify where you found this lead.';
      }
    }
    return null;
  };

  // -------------------- Phase 4b — file staging --------------------
  // Files are held in component state until the lead is created, then
  // uploaded in one batch against /leads/<newId>/documents. Client-side
  // validation mirrors DocumentsCard / backend; server remains the
  // authoritative validator.
  const handleStagedFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    if (stagedFiles.length + picked.length > MAX_FILES_PER_UPLOAD) {
      setFormError(`Max ${MAX_FILES_PER_UPLOAD} files per lead at creation.`);
      e.target.value = '';
      return;
    }
    const tooBig = picked.find(f => f.size > MAX_FILE_SIZE);
    if (tooBig) {
      setFormError(`${tooBig.name} is too large (max 25 MB).`);
      e.target.value = '';
      return;
    }
    const badExt = picked.find(f => !ALLOWED_EXTENSIONS.includes(getExtension(f.name)));
    if (badExt) {
      setFormError(`${badExt.name} has an unsupported type.`);
      e.target.value = '';
      return;
    }
    setFormError('');
    setStagedFiles([...stagedFiles, ...picked]);
    e.target.value = '';
  };

  const removeStagedFile = (idx) => {
    setStagedFiles(stagedFiles.filter((_, i) => i !== idx));
  };

  // Uploads all staged files in a single multipart request against the
  // newly created lead. Returns null on success, error message on failure.
  // Caller (handleSubmit) decides what to do — lead is already created
  // either way, so failures here are "soft" (user can retry from Lead
  // Detail).
  const uploadStagedFiles = async (leadId) => {
    if (stagedFiles.length === 0) return null;
    setUploadProgress(`Uploading ${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'}…`);
    const fd = new FormData();
    stagedFiles.forEach(f => fd.append('files', f));
    if (stagedNote.trim()) fd.append('note', stagedNote.trim());
    try {
      await api.post(`/leads/${leadId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return null;
    } catch (err) {
      return err.response?.data?.error || 'Failed to upload documents.';
    } finally {
      setUploadProgress('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSaving(true);
    try {
      if (editingLead) {
        // Flipping Yes -> No clears the saved note. Confirm before discarding it.
        const wasYes = editingLead.is_note_sent === 1 || editingLead.is_note_sent === true;
        if (wasYes && formData.is_note_sent === false && (editingLead.connection_note || '').trim()) {
          if (!window.confirm('Switching to "No" will clear the saved connection note. Continue?')) {
            setSaving(false);
            return;
          }
        }
        // Edit mode never touches staged files — uploads happen via
        // DocumentsCard on Lead Detail after creation.
        await api.put(`/leads/${editingLead.id}`, formData);
        await fetchAll();
        closeAddForm();
      } else {
        // Create flow: POST lead → upload staged files → redirect.
        const res = await api.post('/leads', formData);
        // Defensive response-shape handling. The createLead controller
        // returns { message, lead_id } — the lead_id field is the
        // authoritative path. The fallbacks cover any future controller
        // shape changes.
        const newId = res.data?.lead_id || res.data?.lead?.id || res.data?.data?.id || res.data?.id;

        if (stagedFiles.length > 0 && newId) {
          const uploadErr = await uploadStagedFiles(newId);
          if (uploadErr) {
            // Lead exists, files failed. Keep modal open with the
            // error so the user can decide; refresh the list so the
            // new lead shows up beneath the modal.
            await fetchAll();
            setFormError(`Lead created, but file upload failed: ${uploadErr} You can retry from the lead detail page.`);
            return;
          }
        }

        await fetchAll();
        closeAddForm();
        // Phase 4b decision: when files were uploaded, jump to Lead
        // Detail so the user can verify them landed. With no files,
        // stay on the list (existing behavior).
        if (stagedFiles.length > 0 && newId) {
          navigate(`/leads/${newId}`);
        }
      }
    } catch (err) {
      console.error('Save lead error:', err);
      setFormError(err.response?.data?.error || 'Failed to save lead.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.prospect_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete lead.');
    }
  };

  const openValidationModal = (lead) => {
    setValidatingLead(lead);
    setValidationClassification(lead.classification);
    setReclassificationNote('');
  };

  const closeValidationModal = () => {
    setValidatingLead(null);
    setValidationClassification('');
    setReclassificationNote('');
  };

  const handleValidate = async () => {
    if (!validationClassification) return;
    try {
      await api.put(`/leads/${validatingLead.id}/validate`, {
        final_classification: validationClassification,
        reclassification_note: reclassificationNote.trim() || null
      });
      await fetchAll();
      closeValidationModal();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to validate lead.');
    }
  };

  const handleReopen = async (lead) => {
    if (!window.confirm(`Reopen "${lead.prospect_name}" for editing? It will go back to pending review.`)) return;
    try {
      await api.put(`/leads/${lead.id}/reopen`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reopen lead.');
    }
  };

  // ============================================================
  // SUB-COMPONENT: Source filter tabs (All | LinkedIn | Upwork)
  // Counts come from GET /leads and reflect the other active filters.
  // ============================================================
  const SourceFilterTabs = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      {SOURCE_TABS.map((t) => {
        const active = filterSource === t.key;
        return (
          <button
            key={t.key || 'all'}
            type="button"
            onClick={() => setFilterSource(t.key)}
            style={{
              cursor: 'pointer', borderRadius: 16, fontSize: 13, fontWeight: 600,
              padding: '6px 14px',
              border: `1px solid ${active ? 'var(--blue)' : 'var(--gray-300)'}`,
              background: active ? 'var(--blue)' : 'transparent',
              color: active ? '#fff' : 'var(--gray-700)',
            }}
          >
            {t.label} <span style={{ opacity: 0.8 }}>{sourceCounts[t.countKey] ?? 0}</span>
          </button>
        );
      })}
    </div>
  );

  // ============================================================
  // SUB-COMPONENT: Pagination footer (Prev / Next + count)
  // Rendered below each table. Hidden entirely when there's nothing to page.
  // ============================================================
  const Pagination = ({ page, meta, onPage }) => {
    const total = meta?.total ?? 0;
    const totalPages = meta?.total_pages ?? 1;
    if (total === 0) return null;
    const from = (page - 1) * PAGE_SIZE + 1;
    const to = Math.min(page * PAGE_SIZE, total);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginTop: 16, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
          {from}–{to} of {total} lead{total === 1 ? '' : 's'}
        </span>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
            >
              Prev
            </button>
            <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================================
  // SUB-COMPONENT: Lead row
  // ============================================================
  const LeadRow = ({ lead, showBde = false, showActions = 'bde' }) => {
    const isLocked = lead.validation_status === 'validated';
    const wasReclassified = lead.original_classification && lead.original_classification !== lead.classification;

    return (
      <tr>
        <td>
          <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{lead.prospect_name}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {lead.company}
            {SOURCE_BADGE[lead.source] && (
              <span className={`badge ${SOURCE_BADGE[lead.source].cls}`} style={{ fontSize: 10, fontWeight: 600 }}>
                {SOURCE_BADGE[lead.source].label}
              </span>
            )}
            {!!lead.is_ai_rescue && (
              <span
                className="badge"
                style={{ fontSize: 10, fontWeight: 700, background: 'rgba(124,58,237,0.12)', color: '#7C3AED',
                         display: 'inline-flex', alignItems: 'center', gap: 3 }}
                title={lead.ai_rescue_source
                  ? `AI Rescue — found via ${lead.ai_rescue_source === 'Other' ? (lead.ai_rescue_source_other || 'Other') : lead.ai_rescue_source}`
                  : 'AI Rescue lead'}
              >
                <Sparkles size={10} /> AI Rescue
              </span>
            )}
          </div>
          {lead.updated_at && (
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>
              Updated {formatRelative(lead.updated_at)}
            </div>
          )}
        </td>
        {showBde && <td style={{ fontSize: 13, color: 'var(--gray-700)' }}>{lead.bde_name}</td>}
        <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{lead.industry || '—'}</td>
        <td>
          <span className={`badge ${CLASSIFICATION_BADGE[lead.classification]}`}>
            {CLASSIFICATION_LABELS[lead.classification]}
          </span>
          {wasReclassified && (
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
              (was {CLASSIFICATION_LABELS[lead.original_classification]})
            </div>
          )}
        </td>
        <td>
          {!isLocked ? (
            <span className="badge badge-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={12} /> Pending Review
            </span>
          ) : (
            <span className={`badge ${LEAD_STATUS_BADGE[lead.lead_status] || 'badge-gray'}`}>
              {LEAD_STATUS_LABELS[lead.lead_status] || 'Active'}
            </span>
          )}
        </td>
        <td style={{ fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {formatUSD(lead.deal_value)}
        </td>
        <td style={{ fontSize: 13, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{formatDate(lead.lead_date)}</td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a
              href={lead.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--blue)', display: 'inline-flex' }}
              title="Open LinkedIn profile"
            >
              <ExternalLink size={16} />
            </a>
            <button
              onClick={() => {
                try {
                  sessionStorage.setItem(STATE_KEY, JSON.stringify({
                    activeTab, filterClassification, filterLeadStatus, filterBde, searchQuery,
                    scrollY: window.scrollY,
                  }));
                } catch {}
                navigate(`/leads/${lead.id}`);
              }}
              style={{ background: 'none', border: 'none', color: 'var(--gray-600)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
              title="View lead detail"
            >
              <Eye size={16} />
            </button>
            {!isReadOnly && showActions === 'bde' && !isLocked && (
              <>
                <button
                  onClick={() => openEditForm(lead)}
                  style={{ background: 'none', border: 'none', color: 'var(--gray-600)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
                  title="Edit lead"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(lead)}
                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
                  title="Delete lead"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            {!isReadOnly && showActions === 'bde' && isLocked && (
              <span style={{ color: 'var(--gray-500)', display: 'inline-flex' }} title="Locked — ask Amit/Admin to reopen">
                <Lock size={16} />
              </span>
            )}
            {!isReadOnly && showActions === 'reviewer' && (
              <>
                <button
                  onClick={() => openEditForm(lead)}
                  style={{ background: 'none', border: 'none', color: 'var(--gray-600)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
                  title="Edit lead"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(lead)}
                  style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
                  title="Delete lead"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
            {!isReadOnly && showActions === 'reviewer' && !isLocked && (
              <button onClick={() => openValidationModal(lead)} className="btn btn-primary btn-sm" style={{ marginLeft: 8 }}>
                Review
              </button>
            )}
            {!isReadOnly && showActions === 'reviewer' && isLocked && (
              <button
                onClick={() => handleReopen(lead)}
                className="btn btn-secondary btn-sm"
                style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <RotateCcw size={12} /> Reopen
              </button>
            )}
          </div>
          {lead.notes && (
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8, maxWidth: 480, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={lead.notes}>{lead.notes}</div>
          )}
        </td>
      </tr>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Users size={24} color="var(--blue)" /> Leads
          </h2>
          <p>Track prospects through Hot / Warm / Cold classification</p>
        </div>
        {!isReadOnly && (
          <button onClick={openAddForm} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Plus size={16} /> Add Lead
          </button>
        )}
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--light-red)', color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {filterStalled && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', marginBottom: 12,
          background: '#FEF3C7', color: '#92400E',
          border: '1px solid #FDE68A', borderRadius: 999, fontSize: 13,
        }}>
          Showing stalled only (no row update in 7+ days)
          <button
            onClick={() => setFilterStalled(false)}
            style={{
              background: 'none', border: 'none', color: '#92400E',
              cursor: 'pointer', fontWeight: 600, padding: 0, fontSize: 13,
            }}
          >Clear</button>
        </div>
      )}

      {canSeeAllLeads && (
        <div style={{ borderBottom: '1px solid var(--gray-200)', marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <button
              onClick={() => setActiveTab('pending')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 4px', fontWeight: 600, fontSize: 14,
                color: activeTab === 'pending' ? 'var(--blue)' : 'var(--gray-500)',
                borderBottom: activeTab === 'pending' ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -1
              }}
            >
              Pending Review {pendingLeads.length > 0 && (
                <span className="badge badge-blue" style={{ marginLeft: 6 }}>{pendingLeads.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('all')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '12px 4px', fontWeight: 600, fontSize: 14,
                color: activeTab === 'all' ? 'var(--blue)' : 'var(--gray-500)',
                borderBottom: activeTab === 'all' ? '2px solid var(--blue)' : '2px solid transparent',
                marginBottom: -1
              }}
            >
              All Leads
            </button>
            {!isReadOnly && (
              <button
                onClick={() => setActiveTab('mine')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '12px 4px', fontWeight: 600, fontSize: 14,
                  color: activeTab === 'mine' ? 'var(--blue)' : 'var(--gray-500)',
                  borderBottom: activeTab === 'mine' ? '2px solid var(--blue)' : '2px solid transparent',
                  marginBottom: -1
                }}
              >
                My Leads
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="empty-state"><p>Loading leads...</p></div>
      ) : (
        <>
          {canSeeAllLeads && activeTab ==='pending' && (
            <div className="card">
              {pendingLeads.length === 0 ? (
                <div className="empty-state">
                  <Check size={32} color="var(--green)" />
                  <p>No leads pending review. You're all caught up.</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Prospect</th>
                        <th>BDE</th>
                        <th>Industry</th>
                        <th>BDE's Classification</th>
                        <th>Status</th>
                        <th style={{ textAlign: 'right' }}>Deal Value</th>
                        <th>Lead Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingLeads.map((lead) => (
                        <LeadRow key={lead.id} lead={lead} showBde={true} showActions="reviewer" />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {canSeeAllLeads && activeTab ==='all' && (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                {canAccessUpwork && <SourceFilterTabs />}
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <Search
                    size={16}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', pointerEvents: 'none' }}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by prospect name or company..."
                    style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Classification</label>
                    <select value={filterClassification} onChange={(e) => setFilterClassification(e.target.value)}>
                      <option value="">All classifications</option>
                      <option value="hot">Hot</option>
                      <option value="warm">Warm</option>
                      <option value="cold">Cold</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Lead Status</label>
                    <select value={filterLeadStatus} onChange={(e) => setFilterLeadStatus(e.target.value)}>
                      <option value="active,discovery_scheduled,discovery_done,proposal_sent,negotiation">Active deals</option>
                      <option value="active">Active only</option>
                      <option value="discovery_scheduled">Discovery scheduled</option>
                      <option value="discovery_done">Discovery done</option>
                      <option value="proposal_sent">Proposal sent</option>
                      <option value="negotiation">Negotiation</option>
                      <option value="closed_won">Closed won</option>
                      <option value="closed_lost">Closed lost</option>
                      <option value="not_interested">Not interested</option>
                      <option value="dead_cold">Dead cold</option>
                      <option value="parked">Parked</option>
                      <option value="">Show all</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>BDE</label>
                    <select value={filterBde} onChange={(e) => setFilterBde(e.target.value)}>
                      <option value="">All BDEs</option>
                      {bdeList.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>AI Rescue</label>
                    <select value={filterAiRescue} onChange={(e) => setFilterAiRescue(e.target.value)}>
                      <option value="">All leads</option>
                      <option value="1">AI Rescue only</option>
                      <option value="0">Non-rescue only</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>AI Rescue Source</label>
                    <select value={filterAiRescueSource} onChange={(e) => setFilterAiRescueSource(e.target.value)}>
                      <option value="">All sources</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Upwork">Upwork</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card">
                {allLeads.length === 0 ? (
                  <div className="empty-state"><p>No leads found.</p></div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Prospect</th>
                          <th>BDE</th>
                          <th>Industry</th>
                          <th>Classification</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Deal Value</th>
                          <th>Lead Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allLeads.map((lead) => (
                          <LeadRow key={lead.id} lead={lead} showBde={true} showActions="reviewer" />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Pagination page={allLeadsPage} meta={allLeadsMeta} onPage={setAllLeadsPage} />
              </div>
            </>
          )}

          {(!canSeeAllLeads || activeTab === 'mine') && (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                {canAccessUpwork && <SourceFilterTabs />}
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <Search
                    size={16}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', pointerEvents: 'none' }}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by prospect name or company..."
                    style={{ width: '100%', padding: '8px 12px 8px 36px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Lead Status</label>
                  <select value={filterLeadStatus} onChange={(e) => setFilterLeadStatus(e.target.value)}>
                    <option value="active,discovery_scheduled,discovery_done,proposal_sent,negotiation">Active deals</option>
                    <option value="active">Active only</option>
                    <option value="discovery_scheduled">Discovery scheduled</option>
                    <option value="discovery_done">Discovery done</option>
                    <option value="proposal_sent">Proposal sent</option>
                    <option value="negotiation">Negotiation</option>
                    <option value="closed_won">Closed won</option>
                    <option value="closed_lost">Closed lost</option>
                    <option value="not_interested">Not interested</option>
                    <option value="dead_cold">Dead cold</option>
                    <option value="parked">Parked</option>
                    <option value="">Show all</option>
                  </select>
                </div>
                <div className="form-row" style={{ marginTop: 12 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>AI Rescue</label>
                    <select value={filterAiRescue} onChange={(e) => setFilterAiRescue(e.target.value)}>
                      <option value="">All leads</option>
                      <option value="1">AI Rescue only</option>
                      <option value="0">Non-rescue only</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>AI Rescue Source</label>
                    <select value={filterAiRescueSource} onChange={(e) => setFilterAiRescueSource(e.target.value)}>
                      <option value="">All sources</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Upwork">Upwork</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="card">
                {myLeads.length === 0 ? (
                  <div className="empty-state">
                    <Users size={32} />
                    <p>{searchQuery ? 'No leads match your search.' : 'No leads yet. Click "Add Lead" to get started.'}</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Prospect</th>
                          <th>Industry</th>
                          <th>Classification</th>
                          <th>Status</th>
                          <th style={{ textAlign: 'right' }}>Deal Value</th>
                          <th>Lead Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myLeads.map((lead) => (
                          <LeadRow key={lead.id} lead={lead} showActions="bde" />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Pagination page={myLeadsPage} meta={myLeadsMeta} onPage={setMyLeadsPage} />
              </div>
            </>
          )}
        </>
      )}

      {/* ============== ADD/EDIT LEAD MODAL ============== */}
      {showAddForm && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeAddForm(); }}
        >
          <div className="card" style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <h3>{editingLead ? 'Edit Lead' : 'Add New Lead'}</h3>
              <button
                onClick={closeAddForm}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', display: 'inline-flex', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            {formError && (
              <div style={{
                padding: 12, marginBottom: 16,
                background: 'var(--light-red)', color: 'var(--red)',
                borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14
              }}>
                <AlertCircle size={16} /> {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Prospect Name *</label>
                <input
                  type="text"
                  value={formData.prospect_name}
                  onChange={(e) => setFormData({ ...formData, prospect_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>LinkedIn URL *</label>
                <input
                  type="text"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://www.linkedin.com/in/..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Company *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Industry</label>
                  <select
                    value={industryIsOther ? '__other__' : (formData.industry || '')}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__other__') {
                        setIndustryIsOther(true);
                        setFormData({ ...formData, industry: '' });
                      } else {
                        setIndustryIsOther(false);
                        setFormData({ ...formData, industry: val });
                      }
                    }}
                  >
                    <option value="">Select industry...</option>
                    {INDUSTRY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    <option value="__other__">Other</option>
                  </select>
                  {industryIsOther && (
                    <input
                      type="text"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="Enter industry..."
                      style={{ marginTop: 8 }}
                      autoFocus
                    />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Classification *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {['hot', 'warm', 'cold'].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFormData({ ...formData, classification: c })}
                      className={`badge ${CLASSIFICATION_BADGE[c]}`}
                      style={{
                        cursor: 'pointer', border: 'none',
                        padding: '8px 16px', fontSize: 14, fontWeight: 600,
                        opacity: formData.classification === c ? 1 : 0.5,
                        outline: formData.classification === c ? '2px solid var(--navy)' : 'none',
                        outlineOffset: 2
                      }}
                    >
                      {CLASSIFICATION_LABELS[c]}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}>
                  Hot = real project, real need now. Warm = interested but no current project. Cold = disengaged.
                </p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lead Date</label>
                  <input
                    type="date"
                    value={formData.lead_date}
                    onChange={(e) => setFormData({ ...formData, lead_date: e.target.value })}
                    max={todayISO()}
                  />
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                    Defaults to today. Use a past date when backfilling.
                  </p>
                </div>
                <div className="form-group">
                  <label>Source *</label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    required
                  >
                    <option value="linkedin">LinkedIn</option>
                    <option value="upwork_inbound">Upwork Inbound</option>
                    <option value="upwork_research">Upwork Research</option>
                  </select>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                    Where this prospect came from. Defaults to LinkedIn.
                  </p>
                </div>
              </div>

              {/* Required only for Upwork sources — the originating job posting. */}
              {(formData.source === 'upwork_inbound' || formData.source === 'upwork_research') && (
                <div className="form-group">
                  <label>Upwork Job URL *</label>
                  <input
                    type="text"
                    value={formData.upwork_job_url}
                    onChange={(e) => setFormData({ ...formData, upwork_job_url: e.target.value })}
                    placeholder="https://www.upwork.com/jobs/..."
                  />
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                    Required for Upwork leads — paste the originating Upwork job posting URL.
                  </p>
                </div>
              )}

              {/* Phase 2: conversation is structured. On CREATE, allow an
                  optional initial paste (parsed into timestamped messages). On
                  EDIT, the conversation is managed per-message on the Lead
                  Detail page, so the bulk textarea is hidden. */}
              {!editingLead ? (
                <div className="form-group">
                  <label>Initial conversation (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={10}
                    placeholder={"Paste the conversation so far. Use 'BDE:' and 'Prospect:' labels, one blank line between exchanges:\n\nBDE: <message>\n\nProspect: <reply>"}
                  />
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                    Parsed into individual messages. You'll add/edit messages one at a time afterward on the lead's page.
                  </p>
                </div>
              ) : (
                <div className="form-group">
                  <label>Conversation</label>
                  <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                    Add, edit, or delete individual messages on the lead's detail page (each is timestamped).
                  </p>
                </div>
              )}

              {/* Connection-note tracking. No default on create (must pick). When
                  editing an un-backfilled lead, a third "Not set" (null) option shows. */}
              <div className="form-group">
                <label>Is note sent? *</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {[
                    { val: true, label: 'Yes' },
                    { val: false, label: 'No' },
                    ...(editingLead ? [{ val: null, label: 'Not set' }] : []),
                  ].map((opt) => (
                    <button
                      key={String(opt.val)}
                      type="button"
                      onClick={() => setFormData({ ...formData, is_note_sent: opt.val })}
                      className="badge badge-gray"
                      style={{
                        cursor: 'pointer', border: 'none',
                        padding: '8px 16px', fontSize: 14, fontWeight: 600,
                        opacity: formData.is_note_sent === opt.val ? 1 : 0.5,
                        outline: formData.is_note_sent === opt.val ? '2px solid var(--navy)' : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}>
                  Was a LinkedIn connection note sent with the connection request?
                </p>
                {formData.is_note_sent === true && (
                  <div style={{ marginTop: 12 }}>
                    <label>Connection note message *</label>
                    <textarea
                      value={formData.connection_note}
                      onChange={(e) => setFormData({ ...formData, connection_note: e.target.value })}
                      rows={4}
                      placeholder="Paste the exact note message you sent with the connection request"
                    />
                  </div>
                )}
              </div>

              {/* ============== AI Rescue flag + conditional discovery source ============== */}
              <div className="form-group">
                <label
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 0 }}
                >
                  {/* Explicit size + reset padding/margin: the global
                      `.form-group input { width:100%; padding:10px 14px }` rule
                      would otherwise stretch the checkbox across the row. */}
                  <input
                    type="checkbox"
                    checked={formData.is_ai_rescue}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // Toggle OFF clears both conditional fields in form state.
                      setFormData({
                        ...formData,
                        is_ai_rescue: on,
                        ai_rescue_source: on ? formData.ai_rescue_source : '',
                        ai_rescue_source_other: on ? formData.ai_rescue_source_other : '',
                      });
                    }}
                    style={{ width: 16, height: 16, padding: 0, margin: 0, flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-800)' }}>AI Rescue Lead</span>
                </label>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6, marginBottom: 0 }}>
                  Turn on if this lead was approached with the AI-rescue angle (built on an AI or no-code
                  builder, needs production engineering). Not just sourced from a builder list.
                </p>

                {formData.is_ai_rescue && (
                  <div style={{ marginTop: 12 }}>
                    <label>Where did you find this lead? *</label>
                    <select
                      value={formData.ai_rescue_source}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Leaving 'Other' clears the free-text detail.
                        setFormData({
                          ...formData,
                          ai_rescue_source: v,
                          ai_rescue_source_other: v === 'Other' ? formData.ai_rescue_source_other : '',
                        });
                      }}
                    >
                      <option value="">Select a source…</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Upwork">Upwork</option>
                      <option value="Other">Other</option>
                    </select>

                    {formData.ai_rescue_source === 'Other' && (
                      <div style={{ marginTop: 12 }}>
                        <label>Please specify *</label>
                        <input
                          type="text"
                          value={formData.ai_rescue_source_other}
                          onChange={(e) => setFormData({ ...formData, ai_rescue_source_other: e.target.value })}
                          placeholder="e.g. Reddit, Twitter, Facebook"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ============== Phase 4b — document staging (create-only) ============== */}
              {!editingLead && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Paperclip size={14} /> Documents <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12 }}>(optional)</span>
                  </label>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2, marginBottom: 8 }}>
                    Attach any prospect-shared files (scope, NDA, references). Up to {MAX_FILES_PER_UPLOAD} files, 25 MB each. PDF, DOCX, XLSX, PPTX, PNG, JPG, CSV, TXT, ZIP.
                  </p>

                  <input
                    ref={stagedFileInputRef}
                    type="file"
                    multiple
                    accept={ACCEPT_ATTR}
                    onChange={handleStagedFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => stagedFileInputRef.current && stagedFileInputRef.current.click()}
                    className="btn btn-secondary btn-sm"
                    disabled={saving || stagedFiles.length >= MAX_FILES_PER_UPLOAD}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Upload size={14} /> Add documents
                  </button>

                  {stagedFiles.length > 0 && (
                    <div style={{
                      marginTop: 12, padding: 10, background: 'var(--gray-50, #F9FAFB)',
                      border: '1px solid var(--gray-200, #E5E7EB)', borderRadius: 6,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700, #374151)', marginBottom: 6 }}>
                        Ready to attach ({stagedFiles.length} file{stagedFiles.length === 1 ? '' : 's'})
                      </div>
                      {stagedFiles.map((f, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 8, padding: '4px 0', fontSize: 12, color: 'var(--gray-700, #374151)',
                        }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            • {f.name} <span style={{ color: 'var(--gray-500, #9CA3AF)' }}>({formatFileSize(f.size)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeStagedFile(i)}
                            disabled={saving}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'var(--gray-500, #6B7280)', padding: 2, display: 'inline-flex',
                            }}
                            title="Remove"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <textarea
                        value={stagedNote}
                        onChange={(e) => setStagedNote(e.target.value)}
                        rows={2}
                        placeholder="Optional context note for all attached files (e.g. 'Scope doc from initial outreach')"
                        disabled={saving}
                        style={{
                          width: '100%', marginTop: 8, padding: '6px 8px', fontSize: 12,
                          border: '1px solid var(--gray-300, #D1D5DB)', borderRadius: 4,
                          fontFamily: 'inherit', resize: 'vertical',
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button type="button" onClick={closeAddForm} disabled={saving} className="btn btn-secondary">Cancel</button>
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {uploadProgress
                    ? uploadProgress
                    : saving
                      ? 'Saving…'
                      : editingLead
                        ? 'Save Changes'
                        : (stagedFiles.length > 0 ? `Add Lead & Upload ${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'}` : 'Add Lead')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============== VALIDATION MODAL ============== */}
      {validatingLead && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 16
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeValidationModal(); }}
        >
          <div className="card" style={{ maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <h3>Review Lead</h3>
              <button
                onClick={closeValidationModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', display: 'inline-flex', padding: 4 }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ background: 'var(--gray-100)', padding: 12, borderRadius: 6, marginBottom: 16 }}>
              <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{validatingLead.prospect_name}</div>
              <div style={{ fontSize: 13, color: 'var(--gray-700)' }}>
                {validatingLead.company}{validatingLead.industry && ` · ${validatingLead.industry}`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>Added by {validatingLead.bde_name}</div>
              <div style={{ marginTop: 8 }}>
                <a
                  href={validatingLead.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13, color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <ExternalLink size={12} /> View LinkedIn profile
                </a>
              </div>
              {validatingLead.notes && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--gray-200)', fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>
                  {validatingLead.notes}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 8 }}>
                BDE classified this as <strong>{CLASSIFICATION_LABELS[validatingLead.classification]}</strong>. Confirm or change:
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['hot', 'warm', 'cold'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setValidationClassification(c)}
                    className={`badge ${CLASSIFICATION_BADGE[c]}`}
                    style={{
                      cursor: 'pointer', border: 'none',
                      padding: '8px 16px', fontSize: 14, fontWeight: 600,
                      opacity: validationClassification === c ? 1 : 0.5,
                      outline: validationClassification === c ? '2px solid var(--navy)' : 'none',
                      outlineOffset: 2
                    }}
                  >
                    {CLASSIFICATION_LABELS[c]}
                  </button>
                ))}
              </div>
            </div>

            {validationClassification && validationClassification !== validatingLead.classification && (
              <div className="form-group">
                <label>Why are you reclassifying? (optional, helpful for the BDE)</label>
                <textarea
                  value={reclassificationNote}
                  onChange={(e) => setReclassificationNote(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Prospect just asked for the case study, not a project. Treat as Warm."'
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={closeValidationModal} className="btn btn-secondary">Cancel</button>
              <button
                onClick={handleValidate}
                disabled={!validationClassification}
                className="btn btn-primary"
              >
                {validationClassification === validatingLead.classification ? 'Confirm & Validate' : 'Reclassify & Validate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
